<script setup lang="ts">
import { computed } from 'vue';
import type { MechanicWorkload } from 'src/types/trello-board-type';

interface Props {
  mechanicBreakdown: MechanicWorkload[];
  unassignedHours: number;
  workload: number | null;
  columnTitle: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const HOURS_PER_MECHANIC = 8;

const sortedMechanics = computed(() =>
  [...props.mechanicBreakdown].sort((a, b) => b.percentage - a.percentage),
);

const totalAvailable = computed(() => props.mechanicBreakdown.length * HOURS_PER_MECHANIC);
const totalUsed = computed(() =>
  props.mechanicBreakdown.reduce((sum, m) => sum + m.scheduledHours, 0),
);

function getSeverityClasses(percentage: number): { text: string; bar: string } {
  if (percentage >= 100) return { text: 'text-red-600 dark:text-red-400', bar: 'bg-red-500' };
  if (percentage >= 80) return { text: 'text-amber-600 dark:text-amber-400', bar: 'bg-amber-500' };
  return { text: 'text-green-600 dark:text-green-400', bar: 'bg-green-500' };
}
</script>

<template>
  <div
    class="absolute top-full left-0 mt-1 w-72 z-30 bg-surface-0 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg shadow-lg transition-all duration-200"
    data-testid="workload-breakdown-panel"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-3 py-2 border-b border-surface-200 dark:border-surface-700">
      <div class="flex items-center gap-2 min-w-0">
        <i class="pi pi-chart-bar text-xs text-primary-500" />
        <span class="text-sm font-semibold text-surface-700 dark:text-surface-200 truncate">
          {{ columnTitle }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-surface-500 dark:text-surface-400 whitespace-nowrap">
          {{ totalUsed.toFixed(1) }}h / {{ totalAvailable.toFixed(1) }}h
        </span>
        <button
          type="button"
          class="p-1 rounded hover:bg-surface-200 dark:hover:bg-surface-600 transition-colors"
          data-testid="workload-breakdown-close"
          @click="emit('close')"
        >
          <i class="pi pi-times text-xs text-surface-500 dark:text-surface-400" />
        </button>
      </div>
    </div>

    <!-- Mechanic rows -->
    <div class="max-h-64 overflow-y-auto">
      <div
        v-for="mechanic in sortedMechanics"
        :key="mechanic.id"
        class="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-50 dark:hover:bg-surface-700/50"
        data-testid="mechanic-row"
      >
        <span class="text-xs text-surface-700 dark:text-surface-300 truncate flex-1 min-w-0">
          {{ mechanic.name ?? mechanic.id }}
        </span>
        <span class="text-xs text-surface-500 dark:text-surface-400 whitespace-nowrap w-20 text-right">
          {{ mechanic.scheduledHours.toFixed(1) }}h / {{ HOURS_PER_MECHANIC }}.0h
        </span>
        <span
          class="text-xs font-semibold w-10 text-right whitespace-nowrap"
          :class="getSeverityClasses(mechanic.percentage).text"
          data-testid="mechanic-percentage"
        >
          {{ Math.round(mechanic.percentage) }}%
        </span>
        <div class="w-16 h-1.5 bg-surface-200 dark:bg-surface-600 rounded-full overflow-hidden flex-none">
          <div
            data-testid="mechanic-progress-fill"
            class="h-full rounded-full transition-all duration-300"
            :class="getSeverityClasses(mechanic.percentage).bar"
            :style="`width: ${Math.min(mechanic.percentage, 100)}%`"
          />
        </div>
      </div>

      <!-- Unassigned row -->
      <div
        v-if="unassignedHours > 0"
        class="flex items-center gap-2 px-3 py-1.5 border-t border-surface-200 dark:border-surface-700"
        data-testid="unassigned-row"
      >
        <span class="text-xs text-amber-600 dark:text-amber-400 italic flex-1 min-w-0">
          Unassigned
        </span>
        <span class="text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap w-20 text-right">
          {{ unassignedHours.toFixed(1) }}h
        </span>
        <span class="w-10" />
        <div class="w-16 h-1.5 rounded-full overflow-hidden flex-none"
          style="background: repeating-linear-gradient(45deg, var(--p-amber-500) 0px, var(--p-amber-500) 2px, var(--p-amber-200) 2px, var(--p-amber-200) 4px);"
        />
      </div>
    </div>
  </div>
</template>
